import React, { useEffect, useRef } from 'react'
import { Input as RInput, InputGroup } from 'rsuite';
import ArrowUpIcon from '@rsuite/icons/ArrowUp';
import ArrowDownIcon from '@rsuite/icons/ArrowDown';
import './styles.scss'

enum keyCodes {
    BACKSPACE = 8,
    DELETE = 46,
    ZERO = 48,
    NINE = 57,
    CTRL_A = 65,
    CTRL_C = 67,
    CTRL_V = 86,
    MINUS = 45,
    NUMPAD_0 = 96,
    NUMPAD_9 = 105,
    DOT = 190,
    NUMPAD_DOT = 110
};

enum stepTypes {
    PLUS = 'plus',
    MINUS = 'minus'
}

const Input = ({ component, onChange }: any) => {
    let value: any;
    const inputRef: any = useRef();
    if (component.valueID) {
        value = component[component.valueID]
    } else {
        value = component.val
    }
    const allowOnlyNumbers = (e: React.KeyboardEvent<HTMLElement>) => {
        
        const keyCode = e.which ? e.which : e.keyCode;
        const keyboardKeys = [keyCodes.BACKSPACE, keyCodes.DELETE];
        const dotsRule = [keyCodes.DOT, keyCodes.NUMPAD_DOT]
        const numericRule = (keyCode >= keyCodes.ZERO && keyCode <= keyCodes.NINE) || (keyCode >= keyCodes.NUMPAD_0 && keyCode <= keyCodes.NUMPAD_9)
        const actionRule = (e.ctrlKey && [keyCodes.CTRL_A, keyCodes.CTRL_C, keyCodes.CTRL_V].includes(keyCode))

        const allowedKeys = [...keyboardKeys];
        const isAllowedKey = allowedKeys.includes(keyCode) || numericRule || actionRule || dotsRule;

        /*
            Reason for allowedKey: 
            The behavior you observed, where Firefox allows alphabetic characters to be entered in an input field with the type set to "number," 
            is not a bug but rather a deliberate design choice by the Firefox development team.

            The HTML specification for the number input type is intended to be a control for entering a number, 
            but it does not strictly enforce the input to be numeric. This allows users to enter scientific notation, 
            negative numbers, or any other text that resembles a number, including non-numeric characters.

            The reason behind this leniency is to provide flexibility to the users and developers. 
            Some cases might involve scenarios where the input is more general or where the application may 
            want to perform custom validation rather than relying solely on the browser's native validation. 
            Firefox's approach to allowing non-numeric characters in the number input type aligns with this objective.
        */

        if (!isAllowedKey) {
            return e.preventDefault();
        }
    }

    useEffect(() => {
        if (+value !== +inputRef.current.value) {
            inputRef.current.value = +value
        }
    }, [value]);

    const handleBlur = (e: any) => {
        const val = +e.target.value || 0;
        if (inputRef.current) {
            inputRef.current.value = val
        }
        return onChange(val);
    }
    const handleStep = (type: string) => {
        const step = +component.step || 1;
        let res = 0;
        switch (type) {
            case stepTypes.PLUS:
                res = +inputRef.current.value + +step;
                break;
            case stepTypes.MINUS:
                res = +inputRef.current.value - +step;
                break;
            default:
                break;
        }
        if (!Number.isInteger(res)) {
            res = +res.toFixed(2)
        }
        return onChange(res)
    }

    const coreInput = <RInput
        ref={inputRef}
        onKeyDown={allowOnlyNumbers}
        type="number"
        defaultValue={value | component.defVal}
        onBlur={handleBlur}
    />
    return <InputGroup className='component-input-wrap'>
        {coreInput}
        <div className='arrows-buttons'>
            <button onClick={() => handleStep('plus')}><ArrowUpIcon /></button>
            <button onClick={() => handleStep('minus')}><ArrowDownIcon /></button>
        </div>
        {component.measure && <InputGroup.Addon>{component.measure}</InputGroup.Addon>}
    </InputGroup>
}

export default Input
